import { combineReducers } from 'redux';
import {
  DELETE_ALL_STAGING,
  SET_BID_STAGING_EQUIPMENT,
  SET_BID_STAGING_DEVICES,
  SET_BID_STAGING_POINTS,
  UPDATE_VALIDATION,
  DELETE_STAGING_EQUIPMENT,
  UPDATE_STAGING_EQUIP,
  UPDATE_STAGING_POINTS,
  REMOVE_MULTIPLE_EQUIPMENT,
  CLEAR_STAGING_LOAD_TIMES,
} from '../actions/actiontypes';
import { groupByProperty } from '../../utils/collections';
import { camelize } from '../../utils/strings';

const devicesLoadTime = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === SET_BID_STAGING_DEVICES) {
    return { ...state, [action.buildingId]: action.time };
  }
  if (action.type === CLEAR_STAGING_LOAD_TIMES) {
    const newState = { ...state };
    delete newState[action.buildingId];
    return newState;
  }
  return state;
};

const pointsLoadTime = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === SET_BID_STAGING_POINTS) {
    return { ...state, [action.buildingId]: action.time };
  }
  if (action.type === CLEAR_STAGING_LOAD_TIMES) {
    const newState = { ...state };
    delete newState[action.buildingId];
    return newState;
  }
  return state;
};

const equipmentLoadTime = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === SET_BID_STAGING_EQUIPMENT) {
    return { ...state, [action.buildingId]: action.time };
  }
  if (action.type === CLEAR_STAGING_LOAD_TIMES) {
    const newState = { ...state };
    delete newState[action.buildingId];
    return newState;
  }
  return state;
};

function convertStagingToApp(entity) {
  const newEntity = {
    ...entity,
    modified: new Date(entity.modified),
    created: new Date(entity.created),
  };
  if (entity.equip_ids) newEntity.equipIds = entity.equip_ids.toSorted();
  camelize(newEntity, 'building_id');
  camelize(newEntity, 'last_published', true, (v) => (v === null ? null : new Date(v)));
  camelize(newEntity, 'last_discovery', true, (v) => (v === null ? null : new Date(v)));
  camelize(newEntity, 'staging_point_id');
  camelize(newEntity, 'staging_device_id');
  camelize(newEntity, 'raw_unit_id');
  camelize(newEntity, 'point_type_id');
  camelize(newEntity, 'published_point_id');
  camelize(newEntity, 'equip_id');
  camelize(newEntity, 'equipment_type_id');
  camelize(newEntity, 'published_equipment_id');
  camelize(newEntity, 'equipment_device_id');
  camelize(newEntity, 'staging_id');
  camelize(newEntity, 'device_id');
  camelize(newEntity, 'deployment_id');
  camelize(newEntity, 'subdeployment_type');
  camelize(newEntity, 'published_device_id');
  newEntity.defaultStateMapping = Object.fromEntries(
    (
      newEntity?.properties?.stateText?.map((v, i) => [i + 1, v]) ?? [
        ['0', newEntity?.properties?.inactiveText],
        ['1', newEntity?.properties?.activeText],
      ]
    ).filter((e) => e[1])
  );
  return newEntity;
}

const equipmentByBuilding = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === SET_BID_STAGING_EQUIPMENT) {
    return { ...state, [action.buildingId]: action.equipment.map(convertStagingToApp) };
  }
  if (action.type === UPDATE_STAGING_EQUIP) {
    const newState = { ...state };
    if (!newState[action.buildingId]) {
      newState[action.buildingId] = action.equipment.map(convertStagingToApp);
    } else {
      const updates = action.equipment.map(convertStagingToApp).reduce((o, e) => {
        // eslint-disable-next-line no-param-reassign
        o[e.equipId] = e;
        return o;
      }, {});
      newState[action.buildingId] = newState[action.buildingId].map((e) => {
        const update = updates[e.equipId];
        if (update != null) {
          delete updates[e.equipId];
          return update;
        }
        return e;
      });
      newState[action.buildingId] = newState[action.buildingId].concat(Object.values(updates));
    }
    return newState;
  }
  if (action.type === DELETE_STAGING_EQUIPMENT) {
    const newState = { ...state };
    newState[action.buildingId] = newState[action.buildingId].filter(
      (e) => !action.equipIds.includes(e.equipId)
    );
    return newState;
  }
  if (action.type === REMOVE_MULTIPLE_EQUIPMENT) {
    // unpublish
    const newState = { ...state };
    newState[action.buildingId] = newState[action.buildingId].map((e) => {
      if (action.equipmentIds.includes(e.published_equipment_id)) {
        const { published_equipment_id: _, ...newEquip } = { ...e };
        return newEquip;
      }
      return e;
    });
    return newState;
  }
  return state;
};

const pointsByBuilding = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === SET_BID_STAGING_POINTS) {
    return {
      ...state,
      [action.buildingId]: groupByProperty('topic', action.points.map(convertStagingToApp)),
    };
  }
  if (action.type === UPDATE_STAGING_POINTS) {
    const newState = { ...state };
    const updatesByTopic = groupByProperty('topic', action.points.map(convertStagingToApp));
    if (!newState[action.buildingId]) {
      newState[action.buildingId] = action.updatesByTopic;
    } else {
      newState[action.buildingId] = { ...newState[action.buildingId], ...updatesByTopic };
    }
    return newState;
  }
  return state;
};

const devicesByBuilding = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === SET_BID_STAGING_DEVICES) {
    return { ...state, [action.buildingId]: action.devices.map(convertStagingToApp) };
  }
  return state;
};

// validation reducers

const points = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === UPDATE_VALIDATION && action.points) {
    return { ...state, [action.buildingId]: { ...state[action.buildingId], ...action.points } };
  }
  return state;
};

const equipment = (state = {}, action) => {
  if (action.type === DELETE_ALL_STAGING) return {};
  if (action.type === UPDATE_VALIDATION && action.equipment) {
    return { ...state, [action.buildingId]: { ...state[action.buildingId], ...action.equipment } };
  }
  return state;
};

export default combineReducers({
  devicesLoadTime,
  pointsLoadTime,
  equipmentLoadTime,
  equipmentByBuilding,
  pointsByBuilding,
  devicesByBuilding,
  validation: combineReducers({ points, equipment }),
});
